import React, { useContext, useEffect, useMemo, useState } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";

import {
    Alert,
    Button,
    Card,
    CardHeader,
    Container,
    Row,
    Col,
    Input,
    InputGroup,
    InputGroupText,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    FormGroup,
    Form,
    Label,
} from "reactstrap";

import HusariaHeader from "views/pages/components/HusariaHeader";
import HusariaSelect from "components/HusariaSelect";
import { ThemeContext } from "contexts/ThemeContext.jsx";

import {
    getSite,
    getTenants,
    getDevices,
    getVisitorPasses,
    createVisitorPass,
    deleteVisitorPass,
    deleteVisitorPasses,
    emailVisitorPass,
    revokeVisitorPass,
} from "../tenants/tenantService";

import {
    applyVisitorPassToDevices,
    removeVisitorPassFromDevices,
} from "../devices/deviceService";

import {
    hasResourcePermission,
    PERMISSIONS,
    RESOURCES,
} from "utils/permissions";

const { SearchBar } = Search;

const pagination = paginationFactory({
    page: 1,
    alwaysShowAllBtns: true,
    showTotal: true,
    withFirstAndLast: false,
    sizePerPageRenderer: ({ onSizePerPageChange }) => (
        <div className="dataTables_length" id="datatable-basic_length">
            <label>
                Show{" "}
                <select
                    name="datatable-basic_length"
                    aria-controls="datatable-basic"
                    className="form-control form-control-sm"
                    onChange={(e) => onSizePerPageChange(e.target.value)}
                >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                </select>{" "}
                entries.
            </label>
        </div>
    ),
});

function VisitorPasses() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const [siteId, setSiteId] = useState(null);
    const [loading, setLoading] = useState(true);

    const [visitorPasses, setVisitorPasses] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [devices, setDevices] = useState([]);

    const [alert, setAlert] = useState(null);

    const [createModal, setCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState(null);

    const [selectedHostTenant, setSelectedHostTenant] = useState(null);
    const emptyVisitorPassForm = {
        visitor_name: "",
        visitor_email: "",
        access_scope: "all",
        device_uuids: [],
    };
    const [visitorPassForm, setVisitorPassForm] = useState(emptyVisitorPassForm);
    const [visitorPassDeviceUpdateResult, setVisitorPassDeviceUpdateResult] = useState(null);

    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedVisitorPassDetails, setSelectedVisitorPassDetails] = useState(null);

    const [visitorPassResult, setVisitorPassResult] = useState(null);
    const [emailingVisitorPass, setEmailingVisitorPass] = useState(false);
    const [visitorPassEmailSuccess, setVisitorPassEmailSuccess] = useState(null);

    const emptyVisitorPassNoticeDismissed = {
        created: false,
        applying: false,
        deviceSuccess: false,
        deviceWarning: false,
    };

    const [visitorPassNoticeDismissed, setVisitorPassNoticeDismissed] = useState(
        emptyVisitorPassNoticeDismissed
    );

    const dismissVisitorPassNotice = (noticeKey) => {
        setVisitorPassNoticeDismissed((prev) => ({
            ...prev,
            [noticeKey]: true,
        }));
    };

    const [actionLoading, setActionLoading] = useState(null);
    const [selectedVisitorPasses, setSelectedVisitorPasses] = useState([]);
    const [bulkDeleteModal, setBulkDeleteModal] = useState(false);

    const userData = useMemo(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    }, []);

    // Reuse tenant permissions for now because this feature originally lived under Tenants.
    const VISITOR_PASS_RESOURCE = RESOURCES.HRMS_TENANTS;

    const canCreate = hasResourcePermission(
        userData,
        VISITOR_PASS_RESOURCE,
        PERMISSIONS.CREATE
    );

    const canEdit = hasResourcePermission(
        userData,
        VISITOR_PASS_RESOURCE,
        PERMISSIONS.EDIT
    );

    const canDelete = hasResourcePermission(
        userData,
        VISITOR_PASS_RESOURCE,
        PERMISSIONS.DELETE
    );

    const canManage = canCreate || canEdit || canDelete;

    const tenantOptions = tenants.map((tenant) => ({
        value: tenant.tenant_uuid,
        label: `${tenant.user_name || "Unnamed Tenant"}${tenant.v_number ? ` (${tenant.v_number})` : ""
            }`,
    }));

    const selectedHostTenantUuid = selectedHostTenant?.value || "";

    const formatDate = (value) => {
        if (!value) return "-";

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleString("en-CA", {
            timeZone: "America/Vancouver",
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
    };

    const loadData = async (activeSiteId = siteId) => {
        if (!activeSiteId) {
            setVisitorPasses([]);
            setTenants([]);
            setDevices([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const [passesData, tenantsData, devicesData] = await Promise.all([
                getVisitorPasses(activeSiteId),
                getTenants(activeSiteId),
                getDevices(activeSiteId),
            ]);

            setVisitorPasses(passesData || []);

            const siteTenants = (tenantsData || []).filter(
                (tenant) => String(tenant.site_uuid) === String(activeSiteId)
            );
            setTenants(siteTenants);

            setDevices(devicesData || []);
        } catch (err) {
            console.error("Failed to load visitor passes:", err);
            setAlert({
                color: "danger",
                msg: err.message || "Failed to load visitor passes.",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const site = getSite();
        setSiteId(site.site_uuid || null);

        const handleSiteChange = (event) => {
            setSiteId(event.detail);
        };

        window.addEventListener("siteChanged", handleSiteChange);
        return () => window.removeEventListener("siteChanged", handleSiteChange);
    }, []);

    useEffect(() => {
        setSelectedVisitorPasses([]);
    }, [siteId]);

    useEffect(() => {
        if (!siteId) {
            setVisitorPasses([]);
            setTenants([]);
            setDevices([]);
            setLoading(false);
            return;
        }

        loadData(siteId);
    }, [siteId]);

    const openCreateModal = () => {
        setSelectedHostTenant(null);
        setVisitorPassForm(emptyVisitorPassForm);
        setVisitorPassResult(null);
        setVisitorPassEmailSuccess(null);
        setCreateError(null);
        setCreateModal(true);
        setVisitorPassDeviceUpdateResult(null);
        setVisitorPassNoticeDismissed(emptyVisitorPassNoticeDismissed);
    };

    const closeCreateModal = () => {
        setCreateModal(false);
        setSelectedHostTenant(null);
        setVisitorPassForm(emptyVisitorPassForm);
        setVisitorPassResult(null);
        setVisitorPassEmailSuccess(null);
        setCreateError(null);
        setCreateLoading(false);
        setVisitorPassDeviceUpdateResult(null);
        setVisitorPassNoticeDismissed(emptyVisitorPassNoticeDismissed);
    };

    const handleDeviceToggle = (deviceUuid) => {
        setVisitorPassForm((prev) => {
            const alreadySelected = prev.device_uuids.includes(deviceUuid);

            return {
                ...prev,
                device_uuids: alreadySelected
                    ? prev.device_uuids.filter((id) => id !== deviceUuid)
                    : [...prev.device_uuids, deviceUuid],
            };
        });
    };

    const getVisitorPassGroupUuid = (rowOrResult) => {
        return (
            rowOrResult?.visitor_pass_group_uuid ||
            rowOrResult?.visitor_pass?.visitor_pass_group_uuid ||
            rowOrResult?.visitor_pass_uuid ||
            rowOrResult?.visitor_pass?.visitor_pass_uuid ||
            null
        );
    };

    const isActiveVisitorPass = (row) => {
        return row?.status === "active" || row?.status === "partially_used";
    };

    const getDeviceUpdateErrors = (result) => {
        if (!result) return [];

        if (Array.isArray(result.errors) && result.errors.length > 0) {
            return result.errors;
        }

        if (Array.isArray(result.results)) {
            return result.results.filter((item) => !item.success);
        }

        return [];
    };

    const getDeviceUpdateErrorMessage = (item) => {
        if (!item) return "Unknown error";

        const deviceName = item.device_name || item.device_uuid || "Device";
        const error = item.error || item.code || "Device update failed";

        return `${deviceName}: ${error}`;
    };

    const openDetailsModal = (row) => {
        setSelectedVisitorPassDetails(row);
        setDetailsModal(true);
    };

    const closeDetailsModal = () => {
        setDetailsModal(false);
        setSelectedVisitorPassDetails(null);
    };

    const getDeviceStatusBadgeClass = (status) => {
        switch (status) {
            case "active":
                return "badge badge-success";
            case "used":
                return "badge badge-primary";
            case "revoked":
                return "badge badge-danger";
            case "expired":
                return "badge badge-secondary";
            default:
                return "badge badge-secondary";
        }
    };
    const handleVisitorPassFormChange = (field, value) => {
        setVisitorPassForm((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleGenerateVisitorPass = async () => {
        if (!selectedHostTenantUuid) {
            setCreateError("Please select a host tenant.");
            return;
        }

        if (!visitorPassForm.visitor_name.trim()) {
            setCreateError("Visitor name is required.");
            return;
        }

        if (!visitorPassForm.visitor_email.trim()) {
            setCreateError("Visitor email is required.");
            return;
        }

        if (
            visitorPassForm.access_scope === "selected" &&
            visitorPassForm.device_uuids.length === 0
        ) {
            setCreateError("Please select at least one device.");
            return;
        }

        setCreateLoading(true);
        setCreateError(null);
        setVisitorPassEmailSuccess(null);
        setVisitorPassDeviceUpdateResult(null);
        setVisitorPassNoticeDismissed(emptyVisitorPassNoticeDismissed);

        try {
            let result;

            try {
                result = await createVisitorPass({
                    site_uuid: siteId,
                    host_tenant_uuid: selectedHostTenantUuid,
                    visitor_name: visitorPassForm.visitor_name.trim(),
                    visitor_email: visitorPassForm.visitor_email.trim(),
                    access_scope: visitorPassForm.access_scope,
                    device_uuids:
                        visitorPassForm.access_scope === "all"
                            ? []
                            : visitorPassForm.device_uuids,
                });
            } catch (err) {
                console.error("Failed to create visitor pass:", err);
                setCreateError(err.message || "Failed to create visitor pass.");
                return;
            }

            // Show QR/PIN immediately once DB creation succeeds.
            setVisitorPassResult(result);

            const groupUuid = getVisitorPassGroupUuid(result);

            if (!groupUuid) {
                const fallbackResult = {
                    success: false,
                    devices_count: result?.devices?.length || 0,
                    applied_count: 0,
                    failed_count: result?.devices?.length || 0,
                    errors: [
                        {
                            error: "Visitor pass was created, but no group UUID was returned.",
                        },
                    ],
                };

                setVisitorPassDeviceUpdateResult(fallbackResult);

                setAlert({
                    color: "warning",
                    msg: "Visitor pass was created, but it could not be added to devices because no group UUID was returned.",
                });

                await loadData(siteId);
                return;
            }

            let applyResult;

            try {
                applyResult = await applyVisitorPassToDevices(groupUuid);
            } catch (err) {
                console.error("Failed to apply visitor pass to devices:", err);

                applyResult = {
                    success: false,
                    devices_count: result?.devices?.length || 0,
                    applied_count: 0,
                    failed_count: result?.devices?.length || 0,
                    errors: [
                        {
                            error:
                                err.message ||
                                "Visitor pass was created, but device access could not be added.",
                        },
                    ],
                };
            }

            setVisitorPassDeviceUpdateResult(applyResult);

            await loadData(siteId);

            if (!applyResult.success) {
                setAlert({
                    color: "warning",
                    msg: `Visitor pass was created, but ${applyResult.failed_count || 0} device(s) could not be updated.`,
                });
            }
        } finally {
            setCreateLoading(false);
        }
    };

    const handleEmailVisitorPass = async (visitorPassUuid = null) => {
        const uuid =
            visitorPassUuid || visitorPassResult?.visitor_pass?.visitor_pass_uuid;

        if (!uuid) return;

        setEmailingVisitorPass(true);
        setCreateError(null);
        setVisitorPassEmailSuccess(null);

        try {
            const result = await emailVisitorPass(uuid);

            if (visitorPassUuid) {
                setAlert({
                    color: "success",
                    msg: result.message || "Visitor pass emailed successfully.",
                });
            } else {
                setVisitorPassEmailSuccess(
                    result.message || "Visitor pass emailed successfully."
                );
            }
        } catch (err) {
            console.error("Failed to email visitor pass:", err);

            if (visitorPassUuid) {
                setAlert({
                    color: "danger",
                    msg: err.message || "Failed to email visitor pass.",
                });
            } else {
                setCreateError(err.message || "Failed to email visitor pass.");
            }
        } finally {
            setEmailingVisitorPass(false);
        }
    };

    const handleRevokeVisitorPass = async (row) => {
        if (!row?.visitor_pass_uuid) return;

        const confirmed = window.confirm(
            `Revoke visitor pass for ${row.visitor_name || "this visitor"}? This will remove visitor access from the device(s).`
        );

        if (!confirmed) return;

        setActionLoading(`revoke-${row.visitor_pass_uuid}`);
        setAlert(null);

        try {
            const groupUuid = getVisitorPassGroupUuid(row);

            await revokeVisitorPass(row.visitor_pass_uuid);

            let removeResult = null;

            if (groupUuid) {
                try {
                    removeResult = await removeVisitorPassFromDevices(groupUuid);
                } catch (err) {
                    console.error("Failed to remove visitor pass from devices:", err);

                    removeResult = {
                        success: false,
                        failed_count: 1,
                        errors: [
                            {
                                error:
                                    err.message ||
                                    "Visitor pass was revoked, but device access could not be removed.",
                            },
                        ],
                    };
                }
            } else {
                removeResult = {
                    success: false,
                    failed_count: 1,
                    errors: [
                        {
                            error: "Missing visitor pass group UUID.",
                        },
                    ],
                };
            }

            await loadData(siteId);

            if (!removeResult.success) {
                setAlert({
                    color: "warning",
                    msg: `Visitor pass was revoked, but ${removeResult.failed_count || 0} device(s) could not be updated.`,
                });
            } else {
                setAlert({
                    color: "success",
                    msg: "Visitor pass revoked and removed from device(s).",
                });
            }
        } catch (err) {
            console.error("Failed to revoke visitor pass:", err);
            setAlert({
                color: "danger",
                msg: err.message || "Failed to revoke visitor pass.",
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeleteVisitorPass = async (row) => {
        if (!row?.visitor_pass_uuid) return;

        const isActive = isActiveVisitorPass(row);

        const confirmed = window.confirm(
            isActive
                ? `Delete active visitor pass for ${row.visitor_name || "this visitor"}? This will remove visitor access from the device(s), then delete it from the table.`
                : `Delete visitor pass for ${row.visitor_name || "this visitor"}? This action cannot be undone.`
        );

        if (!confirmed) return;

        setActionLoading(`delete-${row.visitor_pass_uuid}`);
        setAlert(null);

        try {
            const groupUuid = getVisitorPassGroupUuid(row);

            // For active passes, remove from physical devices BEFORE deleting DB rows.
            // Otherwise we could lose the UUID needed to delete the user from the device.
            if (isActive) {
                if (!groupUuid) {
                    setAlert({
                        color: "warning",
                        msg: "Visitor pass was not deleted because the group UUID is missing.",
                    });
                    return;
                }

                let removeResult;

                try {
                    removeResult = await removeVisitorPassFromDevices(groupUuid);
                } catch (err) {
                    console.error("Failed to remove visitor pass from devices:", err);

                    removeResult = {
                        success: false,
                        failed_count: 1,
                        errors: [
                            {
                                error:
                                    err.message ||
                                    "Device access could not be removed.",
                            },
                        ],
                    };
                }

                if (!removeResult.success) {
                    setAlert({
                        color: "warning",
                        msg: `Visitor pass was not deleted because ${removeResult.failed_count || 0} device(s) could not remove the visitor access.`,
                    });
                    return;
                }
            }

            await deleteVisitorPass(row.visitor_pass_uuid);
            await loadData(siteId);

            setAlert({
                color: "success",
                msg: "Visitor pass deleted successfully.",
            });
        } catch (err) {
            console.error("Failed to delete visitor pass:", err);
            setAlert({
                color: "danger",
                msg: err.message || "Failed to delete visitor pass.",
            });
        } finally {
            setActionLoading(null);
        }
    };

    const handleBulkDeleteVisitorPasses = async () => {
        if (selectedVisitorPasses.length === 0) return;

        const selectedRows = visitorPasses.filter((pass) =>
            selectedVisitorPasses.includes(pass.visitor_pass_uuid)
        );

        if (selectedRows.length === 0) {
            setSelectedVisitorPasses([]);
            setBulkDeleteModal(false);
            return;
        }

        setActionLoading("bulk-delete");
        setAlert(null);

        const readyToDelete = [];
        const skipped = [];

        try {
            for (const pass of selectedRows) {
                const passLabel =
                    pass.visitor_name ||
                    pass.visitor_email ||
                    pass.visitor_pass_uuid;

                if (isActiveVisitorPass(pass)) {
                    const groupUuid = getVisitorPassGroupUuid(pass);

                    if (!groupUuid) {
                        skipped.push(`${passLabel}: missing group UUID`);
                        continue;
                    }

                    let removeResult;

                    try {
                        removeResult = await removeVisitorPassFromDevices(groupUuid);
                    } catch (err) {
                        console.error("Failed to remove visitor pass from devices:", err);
                        skipped.push(
                            `${passLabel}: ${err.message || "device access could not be removed"}`
                        );
                        continue;
                    }

                    if (!removeResult?.success) {
                        skipped.push(
                            `${passLabel}: ${removeResult?.failed_count || 0} device(s) could not remove access`
                        );
                        continue;
                    }
                }

                readyToDelete.push(pass.visitor_pass_uuid);
            }

            if (readyToDelete.length === 0) {
                setAlert({
                    color: "warning",
                    msg: "No visitor passes were deleted because device access could not be removed first.",
                });
                return;
            }

            await deleteVisitorPasses(readyToDelete);
            await loadData(siteId);

            setSelectedVisitorPasses([]);

            if (skipped.length > 0) {
                setAlert({
                    color: "warning",
                    msg: `Deleted ${readyToDelete.length} visitor pass(es). ${skipped.length} selected pass(es) could not be deleted because device access could not be removed first.`,
                });
            } else {
                setAlert({
                    color: "success",
                    msg: `${readyToDelete.length} visitor pass(es) deleted successfully.`,
                });
            }
        } catch (err) {
            console.error("Failed to delete selected visitor passes:", err);
            setAlert({
                color: "danger",
                msg: err.message || "Failed to delete selected visitor passes.",
            });
        } finally {
            setActionLoading(null);
            setBulkDeleteModal(false);
        }
    };

    const selectRow = {
        mode: "checkbox",
        clickToSelect: false,
        selected: selectedVisitorPasses,
        onSelect: (row, isSelect) => {
            setSelectedVisitorPasses((prev) =>
                isSelect
                    ? [...new Set([...prev, row.visitor_pass_uuid])]
                    : prev.filter((id) => id !== row.visitor_pass_uuid)
            );
        },
        onSelectAll: (isSelect, rows) => {
            setSelectedVisitorPasses(
                isSelect ? rows.map((row) => row.visitor_pass_uuid) : []
            );
        },
        classes: "checkbox-column",
        headerClasses: "checkbox-column-header",
    };

    const columns = [
        {
            dataField: "visitor_name",
            text: "Visitor",
            sort: true,
            formatter: (cell) => (
                <span
                    className="text-primary font-weight-bold"
                    style={{ cursor: "pointer" }}
                >
                    {cell || "-"}
                </span>
            ),
        },
        {
            dataField: "visitor_email",
            text: "Email",
            sort: true,
            formatter: (cell) => cell || "-",
        },
        {
            dataField: "host_tenant_name",
            text: "Host Tenant",
            sort: true,
            formatter: (cell) => cell || "-",
        },
        {
            dataField: "device_name",
            text: "Devices",
            sort: true,
            formatter: (cell, row) => {
                if (cell) return cell;
                if (row.device_names) return row.device_names;
                if (!row.device_ip) return "-";
                return `${row.device_name || "Unnamed Device"} (${row.device_ip})`;
            },
        },
        {
            dataField: "status",
            text: "Status",
            sort: true,
            formatter: (cell) => {
                const status = cell || "-";
                const badgeClass =
                    status === "active"
                        ? "badge badge-success"
                        : status === "revoked"
                            ? "badge badge-danger"
                            : "badge badge-secondary";

                return <span className={badgeClass}>{status}</span>;
            },
        },
        {
            dataField: "created_at",
            text: "Created",
            sort: true,
            formatter: (cell) => formatDate(cell),
        },
        {
            dataField: "expires_at",
            text: "Expires",
            sort: true,
            formatter: (cell) => formatDate(cell),
        },
        {
            dataField: "actions",
            text: "Actions",
            hidden: !canManage,
            formatter: (cell, row) => {
                const isActive = row.status === "active";
                const revokeLoading = actionLoading === `revoke-${row.visitor_pass_uuid}`;
                const deleteLoading = actionLoading === `delete-${row.visitor_pass_uuid}`;

                return (
                    <div className="table-actions d-flex" style={{ gap: "8px", flexWrap: "wrap" }}>
                        {canEdit && isActive && (
                            <Button
                                size="sm"
                                color="primary"
                                disabled={emailingVisitorPass}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEmailVisitorPass(row.visitor_pass_uuid);
                                }}
                            >
                                Email QR
                            </Button>
                        )}

                        {canDelete && isActive && (
                            <Button
                                size="sm"
                                color="warning"
                                disabled={revokeLoading}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRevokeVisitorPass(row);
                                }}
                            >
                                {revokeLoading ? "Revoking..." : "Revoke"}
                            </Button>
                        )}

                        {canDelete && (
                            <Button
                                size="sm"
                                color="danger"
                                disabled={deleteLoading}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeleteVisitorPass(row);
                                }}
                            >
                                {deleteLoading ? "Deleting..." : "Delete"}
                            </Button>
                        )}
                    </div>
                );
            },
        }
    ];

    return (
        <>
            <HusariaHeader name="Visitor Passes" parentName="HRMS" padding="4.5rem" />

            <Container fluid className={`mt--6 ${isDark ? "table-dark-mode" : ""}`}>
                <Row>
                    <div className="col">
                        <Card>
                            {alert && (
                                <Alert
                                    color={alert.color}
                                    toggle={() => setAlert(null)}
                                    className="m-3 mb-0"
                                >
                                    {alert.msg}
                                </Alert>
                            )}

                            <ToolkitProvider
                                keyField="visitor_pass_uuid"
                                data={visitorPasses}
                                columns={columns}
                                search
                            >
                                {(props) => (
                                    <>
                                        <CardHeader
                                            className="border-0 header-theme"
                                            style={{
                                                backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                                                color: isDark ? "#e2e8f0" : "#111",
                                            }}
                                        >
                                            <Row className="align-items-center">
                                                <Col xs="6" className="d-flex align-items-center">
                                                    <Form className="navbar-search navbar-search-light form-inline mr-sm-3">
                                                        <FormGroup className="mb-0">
                                                            <InputGroup className="input-group-alternative input-group-merge search-border">
                                                                <InputGroupText>
                                                                    <i
                                                                        className="fas fa-search"
                                                                        style={{
                                                                            color: isDark
                                                                                ? "#b0b0b0"
                                                                                : "rgb(102, 102, 102)",
                                                                        }}
                                                                    />
                                                                </InputGroupText>
                                                                <SearchBar
                                                                    className="form-control table-search-bar"
                                                                    placeholder="Search"
                                                                    {...props.searchProps}
                                                                />
                                                            </InputGroup>
                                                        </FormGroup>
                                                    </Form>
                                                </Col>

                                                <Col
                                                    className="text-right"
                                                    xs="6"
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "flex-end",
                                                        gap: "8px",
                                                    }}
                                                >
                                                    {selectedVisitorPasses.length > 0 && canDelete && (
                                                        <Button
                                                            className="btn-round btn-icon"
                                                            color="danger"
                                                            onClick={() => setBulkDeleteModal(true)}
                                                            size="sm"
                                                            disabled={actionLoading === "bulk-delete"}
                                                        >
                                                            <span className="btn-inner--icon mr-1">
                                                                <i className="fas fa-trash" />
                                                            </span>
                                                            <span className="btn-inner--text">
                                                                Delete Selected ({selectedVisitorPasses.length})
                                                            </span>
                                                        </Button>
                                                    )}
                                                    <Button
                                                        className="btn-round btn-icon"
                                                        color="info"
                                                        onClick={() => loadData(siteId)}
                                                        size="sm"
                                                        disabled={loading}
                                                    >
                                                        <span className="btn-inner--icon mr-1">
                                                            <i className="fas fa-sync" />
                                                        </span>
                                                        <span className="btn-inner--text">
                                                            {loading ? "Refreshing..." : "Refresh"}
                                                        </span>
                                                    </Button>

                                                    {canCreate && (
                                                        <Button
                                                            className="btn-round btn-icon"
                                                            color="success"
                                                            onClick={openCreateModal}
                                                            size="sm"
                                                        >
                                                            <span className="btn-inner--icon mr-1">
                                                                <i className="fas fa-plus" />
                                                            </span>
                                                            <span className="btn-inner--text">
                                                                New Visitor Pass
                                                            </span>
                                                        </Button>
                                                    )}
                                                </Col>
                                            </Row>
                                        </CardHeader>

                                        <div className="py-4 table-responsive">
                                            <BootstrapTable
                                                {...props.baseProps}
                                                bootstrap4
                                                pagination={pagination}
                                                bordered={false}
                                                hover
                                                selectRow={canDelete ? selectRow : undefined}
                                                classes="align-items-center table-flush"
                                                rowEvents={{
                                                    onClick: (e, row) => {
                                                        if (
                                                            e.target.closest("button") ||
                                                            e.target.closest("a") ||
                                                            e.target.closest("input")
                                                        ) {
                                                            return;
                                                        }

                                                        openDetailsModal(row);
                                                    },
                                                }}
                                                rowStyle={{
                                                    cursor: "pointer",
                                                }}
                                                noDataIndication={() =>
                                                    loading ? (
                                                        <div className="text-center">Loading...</div>
                                                    ) : (
                                                        <div className="text-center">
                                                            No visitor passes found for the selected site.
                                                        </div>
                                                    )
                                                }
                                            />
                                        </div>
                                    </>
                                )}
                            </ToolkitProvider>
                        </Card>
                    </div>
                </Row>
            </Container>

            <Modal isOpen={createModal} toggle={closeCreateModal} centered size="lg">
                <ModalHeader
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                    }}
                    toggle={closeCreateModal}
                >
                    Visitor Pass
                </ModalHeader>

                <ModalBody className="card-body">
                    {createError && (
                        <Alert color="danger" toggle={() => setCreateError(null)}>
                            {createError}
                        </Alert>
                    )}

                    {!visitorPassResult ? (
                        <>
                            <FormGroup>
                                <Label for="host_tenant_uuid">
                                    Host Tenant <span className="text-danger">*</span>
                                </Label>

                                <HusariaSelect
                                    name="host_tenant_uuid"
                                    id="host_tenant_uuid"
                                    options={tenantOptions}
                                    value={selectedHostTenant}
                                    onChange={setSelectedHostTenant}
                                    placeholder="Select host tenant..."
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="visitor_name">
                                    Visitor Name <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="visitor_name"
                                    type="text"
                                    placeholder="Enter visitor name"
                                    value={visitorPassForm.visitor_name}
                                    onChange={(e) =>
                                        handleVisitorPassFormChange("visitor_name", e.target.value)
                                    }
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label for="visitor_email">
                                    Visitor Email <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    id="visitor_email"
                                    type="email"
                                    placeholder="Enter visitor email"
                                    value={visitorPassForm.visitor_email}
                                    onChange={(e) =>
                                        handleVisitorPassFormChange("visitor_email", e.target.value)
                                    }
                                />
                            </FormGroup>

                            <FormGroup>
                                <Label>
                                    Access Devices <span className="text-danger">*</span>
                                </Label>

                                <div className="custom-control custom-radio mb-3">
                                    <Input
                                        className="custom-control-input"
                                        id="access_scope_all"
                                        type="radio"
                                        name="access_scope"
                                        checked={visitorPassForm.access_scope === "all"}
                                        onChange={() =>
                                            setVisitorPassForm((prev) => ({
                                                ...prev,
                                                access_scope: "all",
                                                device_uuids: [],
                                            }))
                                        }
                                    />
                                    <Label className="custom-control-label" for="access_scope_all">
                                        All devices for this site
                                    </Label>
                                </div>

                                <div className="custom-control custom-radio mb-2">
                                    <Input
                                        className="custom-control-input"
                                        id="access_scope_selected"
                                        type="radio"
                                        name="access_scope"
                                        checked={visitorPassForm.access_scope === "selected"}
                                        onChange={() =>
                                            setVisitorPassForm((prev) => ({
                                                ...prev,
                                                access_scope: "selected",
                                            }))
                                        }
                                    />
                                    <Label className="custom-control-label" for="access_scope_selected">
                                        Select specific devices
                                    </Label>
                                </div>

                                {visitorPassForm.access_scope === "selected" && (
                                    <div
                                        style={{
                                            maxHeight: "220px",
                                            overflowY: "auto",
                                            border: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                                            borderRadius: "6px",
                                            padding: "12px",
                                        }}
                                    >
                                        {devices.length === 0 ? (
                                            <div className="text-muted">No devices found for this site.</div>
                                        ) : (
                                            devices.map((device) => (
                                                <div
                                                    key={device.device_uuid}
                                                    className="custom-control custom-checkbox mb-2"
                                                >
                                                    <Input
                                                        className="custom-control-input"
                                                        id={`device_${device.device_uuid}`}
                                                        type="checkbox"
                                                        checked={visitorPassForm.device_uuids.includes(
                                                            device.device_uuid
                                                        )}
                                                        onChange={() => handleDeviceToggle(device.device_uuid)}
                                                    />
                                                    <Label
                                                        className="custom-control-label"
                                                        for={`device_${device.device_uuid}`}
                                                    >
                                                        {device.device_name || "Unnamed Device"}{" "}
                                                        {device.device_ip ? `(${device.device_ip})` : ""}
                                                    </Label>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </FormGroup>

                            <div className="text-muted">
                                This will generate one QR code and PIN for the visitor.
                                <br />
                                The visitor will have one-time access to each selected device.
                                <br />
                                If the pass is not used within 24 hours, it will expire.
                            </div>
                        </>
                    ) : (
                        <>
                            {!visitorPassNoticeDismissed.created && (
                                <Alert
                                    color="success"
                                    toggle={() => dismissVisitorPassNotice("created")}
                                >
                                    Visitor pass created successfully.
                                </Alert>
                            )}

                            {createLoading &&
                                !visitorPassDeviceUpdateResult &&
                                !visitorPassNoticeDismissed.applying && (
                                    <Alert
                                        color="info"
                                        toggle={() => dismissVisitorPassNotice("applying")}
                                    >
                                        Visitor pass created. Adding visitor access to selected device(s)...
                                    </Alert>
                                )}

                            {visitorPassDeviceUpdateResult &&
                                visitorPassDeviceUpdateResult.success &&
                                !visitorPassNoticeDismissed.deviceSuccess && (
                                    <Alert
                                        color="success"
                                        toggle={() => dismissVisitorPassNotice("deviceSuccess")}
                                    >
                                        Visitor access added to{" "}
                                        {visitorPassDeviceUpdateResult.applied_count ??
                                            visitorPassDeviceUpdateResult.devices_count ??
                                            0}{" "}
                                        device(s).
                                    </Alert>
                                )}

                            {visitorPassDeviceUpdateResult &&
                                !visitorPassDeviceUpdateResult.success &&
                                !visitorPassNoticeDismissed.deviceWarning && (
                                    <Alert
                                        color="warning"
                                        toggle={() => dismissVisitorPassNotice("deviceWarning")}
                                    >
                                        <div>
                                            <strong>
                                                Visitor pass was created, but some devices could not be updated.
                                            </strong>
                                        </div>

                                        <div className="mt-1">
                                            Added to {visitorPassDeviceUpdateResult.applied_count || 0} of{" "}
                                            {visitorPassDeviceUpdateResult.devices_count || 0} device(s).
                                        </div>

                                        {getDeviceUpdateErrors(visitorPassDeviceUpdateResult).length > 0 && (
                                            <ul className="mt-2 mb-0">
                                                {getDeviceUpdateErrors(visitorPassDeviceUpdateResult)
                                                    .slice(0, 5)
                                                    .map((item, index) => (
                                                        <li key={`${item.device_uuid || "device"}-${index}`}>
                                                            {getDeviceUpdateErrorMessage(item)}
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}

                                        {getDeviceUpdateErrors(visitorPassDeviceUpdateResult).length > 5 && (
                                            <div className="mt-2">
                                                And{" "}
                                                {getDeviceUpdateErrors(visitorPassDeviceUpdateResult).length - 5}{" "}
                                                more device(s).
                                            </div>
                                        )}

                                        <div className="mt-2">
                                            The pass is saved. Any failed devices may need to be updated again.
                                        </div>
                                    </Alert>
                                )}

                            {visitorPassEmailSuccess && (
                                <Alert
                                    color="success"
                                    toggle={() => setVisitorPassEmailSuccess(null)}
                                >
                                    {visitorPassEmailSuccess}
                                </Alert>
                            )}

                            <div className="mb-3">
                                <strong>Visitor:</strong>{" "}
                                {visitorPassResult?.visitor_pass?.visitor_name || "-"}
                            </div>

                            <div className="mb-3">
                                <strong>Email:</strong>{" "}
                                {visitorPassResult?.visitor_pass?.visitor_email || "-"}
                            </div>

                            <div className="mb-3">
                                <strong>Host Tenant:</strong>{" "}
                                {visitorPassResult?.host_tenant?.user_name || "-"}
                            </div>

                            <div className="mb-3">
                                <strong>Device:</strong>{" "}
                                {visitorPassResult?.device?.device_name || "-"}
                            </div>

                            <div className="mb-3 text-center">
                                <img
                                    src={`data:image/png;base64,${visitorPassResult.qr_png_base64}`}
                                    alt="Visitor Pass QR Code"
                                    style={{
                                        maxWidth: "260px",
                                        width: "100%",
                                        height: "auto",
                                        border: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                                        borderRadius: "8px",
                                        padding: "8px",
                                        backgroundColor: "#fff",
                                    }}
                                />
                            </div>

                            <div className="mb-2 text-center">
                                <strong>Access PIN:</strong>
                            </div>

                            <div
                                className="text-center mb-3"
                                style={{
                                    fontSize: "1.5rem",
                                    fontWeight: 700,
                                    letterSpacing: "2px",
                                }}
                            >
                                {visitorPassResult?.qr_value || "-"}
                            </div>

                            <div className="text-muted text-center">
                                This visitor pass is ready. The visitor can use either the QR code or PIN.
                            </div>
                        </>
                    )}
                </ModalBody>

                <ModalFooter className="card-body">
                    <Button color="light" onClick={closeCreateModal}>
                        {visitorPassResult ? "Close" : "Cancel"}
                    </Button>

                    {!visitorPassResult && (
                        <Button
                            color="success"
                            onClick={handleGenerateVisitorPass}
                            disabled={createLoading}
                        >
                            {createLoading ? "Generating..." : "Generate Pass"}
                        </Button>
                    )}

                    {visitorPassResult && (
                        <Button
                            color="primary"
                            onClick={() => handleEmailVisitorPass()}
                            disabled={emailingVisitorPass}
                        >
                            {emailingVisitorPass ? "Sending..." : "Email QR"}
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            <Modal isOpen={bulkDeleteModal} toggle={() => setBulkDeleteModal(false)} centered>
                <ModalHeader
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                    }}
                    toggle={() => setBulkDeleteModal(false)}
                >
                    Delete Visitor Passes
                </ModalHeader>

                <ModalBody className="card-body">
                    Delete <strong>{selectedVisitorPasses.length}</strong> selected visitor pass(es)?
                    <br />
                    Active visitor passes will be removed from device(s) before they are deleted.
                    <br />
                    <strong>This action cannot be undone.</strong>
                </ModalBody>

                <ModalFooter className="card-body">
                    <Button
                        color="light"
                        onClick={() => setBulkDeleteModal(false)}
                        disabled={actionLoading === "bulk-delete"}
                    >
                        Cancel
                    </Button>

                    <Button
                        color="danger"
                        onClick={handleBulkDeleteVisitorPasses}
                        disabled={actionLoading === "bulk-delete"}
                    >
                        {actionLoading === "bulk-delete" ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={detailsModal} toggle={closeDetailsModal} centered size="lg">
                <ModalHeader
                    style={{
                        backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                        color: isDark ? "#e2e8f0" : "#111",
                    }}
                    toggle={closeDetailsModal}
                >
                    Visitor Pass Details
                </ModalHeader>

                <ModalBody className="card-body">
                    {selectedVisitorPassDetails && (
                        <>
                            <Row className="mb-3">
                                <Col md="6">
                                    <div className="mb-2">
                                        <strong>Visitor:</strong>{" "}
                                        {selectedVisitorPassDetails.visitor_name || "-"}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Email:</strong>{" "}
                                        {selectedVisitorPassDetails.visitor_email || "-"}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Host Tenant:</strong>{" "}
                                        {selectedVisitorPassDetails.host_tenant_name || "-"}
                                    </div>
                                </Col>

                                <Col md="6">
                                    <div className="mb-2">
                                        <strong>Access Scope:</strong>{" "}
                                        {selectedVisitorPassDetails.access_scope === "all"
                                            ? "All devices"
                                            : "Selected devices"}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Access PIN:</strong>{" "}
                                        {selectedVisitorPassDetails.access_code || "-"}
                                    </div>

                                    <div className="mb-2">
                                        <strong>Expires:</strong>{" "}
                                        {formatDate(selectedVisitorPassDetails.expires_at)}
                                    </div>
                                </Col>
                            </Row>

                            <div className="table-responsive">
                                <table className="table align-items-center table-flush">
                                    <thead>
                                        <tr>
                                            <th>Device</th>
                                            <th>IP</th>
                                            <th>Status</th>
                                            <th>Used At</th>
                                            <th>Expires</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {(selectedVisitorPassDetails.device_details || []).map(
                                            (device) => (
                                                <tr key={device.visitor_pass_uuid}>
                                                    <td>{device.device_name || "Unnamed Device"}</td>
                                                    <td>{device.device_ip || "-"}</td>
                                                    <td>
                                                        <span
                                                            className={getDeviceStatusBadgeClass(
                                                                device.status
                                                            )}
                                                        >
                                                            {device.status || "-"}
                                                        </span>
                                                    </td>
                                                    <td>{formatDate(device.used_at)}</td>
                                                    <td>{formatDate(device.expires_at)}</td>
                                                </tr>
                                            )
                                        )}

                                        {(selectedVisitorPassDetails.device_details || []).length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    No device details found.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </ModalBody>

                <ModalFooter className="card-body">
                    <Button color="light" onClick={closeDetailsModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
}

export default VisitorPasses;